import {useMemo} from 'react';
import {useQuery} from 'react-query';
import {CompanyCheckinsController} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.controller';
import {CompanyCheckinsRequestImpl} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.request';
import type {CheckinListFilter} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.request';
import {ICheckinCycle} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {SubmissionRecord} from '../components/submission-card';

interface UseCheckinsWithCyclesParams {
  userId?: string;
  isTeamFeed?: boolean; // true for manager view, false for personal view
}

type PopulatedCheckinCycle = Partial<ICheckinCycle> & {id?: string};

export const getPopulatedCheckinCycle = (
  submission: SubmissionRecord,
): PopulatedCheckinCycle | undefined => {
  if (submission.cycle && typeof submission.cycle === 'object') {
    return submission.cycle as PopulatedCheckinCycle;
  }

  const cycleIdValue = (submission as SubmissionRecord & {
    cycleId?: string | PopulatedCheckinCycle;
  }).cycleId;
  return cycleIdValue && typeof cycleIdValue === 'object'
    ? cycleIdValue
    : undefined;
};

export const getCheckinCycleId = (submission: SubmissionRecord) => {
  const populatedCycle = getPopulatedCheckinCycle(submission);
  if (populatedCycle?.id) return populatedCycle.id;
  if (typeof submission.cycle === 'string') return submission.cycle;
  return typeof submission.cycleId === 'string'
    ? submission.cycleId
    : undefined;
};

export const getCheckinCycleName = (submission: SubmissionRecord) =>
  submission.cycleName ||
  getPopulatedCheckinCycle(submission)?.cycleName;

export const hasPopulatedCycleCreator = (submission: SubmissionRecord) =>
  Boolean(
    getCheckinCycleName(submission) &&
      getPopulatedCheckinCycle(submission)?.createdBy,
  );

export const resolveCheckinCycleName = (
  submission: SubmissionRecord,
  cycleNamesById: ReadonlyMap<string, string>,
) => {
  const cycleId = getCheckinCycleId(submission);

  return (
    getCheckinCycleName(submission) ||
    cycleNamesById.get(cycleId || '') ||
    'Check-in cycle'
  );
};

export const useCheckinsWithCycles = ({
  userId,
  isTeamFeed = false,
}: UseCheckinsWithCyclesParams) => {
  const checkinsRequest = useMemo(() => new CompanyCheckinsRequestImpl(), []);
  const checkinsController = useMemo(
    () => new CompanyCheckinsController(checkinsRequest),
    [checkinsRequest],
  );

  // Fetch checkins
  const {
    data: checkinsResponse,
    isLoading: checkinsLoading,
    refetch: checkinsResponseRefetch,
  } = useQuery(
    [isTeamFeed ? 'team-checkins' : 'my-checkins', userId],
    () =>
      checkinsController.getListOfCheckins(
        isTeamFeed
          ? {
              manager: userId,
              paginate: false,
            }
          : {
              user: userId,
              paginate: false,
              isSubmitted: true,
            },
      ),
    {
      enabled: !!userId,
      refetchOnWindowFocus: false,
    },
  );

  // Get raw checkins data
  const allSubmissions = useMemo(() => {
    if (!checkinsResponse) return [];
    return (checkinsResponse.data || []) as SubmissionRecord[];
  }, [checkinsResponse]);

  const lookupFilter = useMemo<CheckinListFilter>(
    () =>
      isTeamFeed
        ? {manager: userId, paginate: false}
        : {user: userId, paginate: false, isSubmitted: true},
    [isTeamFeed, userId],
  );

  const {data: cycleLookupResponse, isLoading: cycleLookupLoading} = useQuery(
    [
      isTeamFeed ? 'team-checkins-cycle-lookup' : 'my-checkins-cycle-lookup',
      userId,
    ],
    async () => {
      const populatedByCycleId = await checkinsController.getListOfCheckins({
        ...lookupFilter,
        include: 'cycleId',
      });
      const rows = populatedByCycleId ? populatedByCycleId.data || [] : [];
      const hasCompleteCycleMetadata = rows.some((row) =>
        hasPopulatedCycleCreator(row as SubmissionRecord),
      );

      if (hasCompleteCycleMetadata) return populatedByCycleId;

      return checkinsController.getListOfCheckins({
        ...lookupFilter,
        include: 'cycle',
      });
    },
    {
      enabled: !!userId,
      refetchOnWindowFocus: false,
    },
  );

  const cycleNamesById = useMemo(() => {
    const names = new Map<string, string>();
    const rows = cycleLookupResponse ? cycleLookupResponse.data || [] : [];

    rows.forEach((row) => {
      const submission = row as SubmissionRecord;
      const cycleId = getCheckinCycleId(submission);
      const cycleName = getCheckinCycleName(submission);
      if (cycleId && cycleName) names.set(cycleId, cycleName);
    });

    return names;
  }, [cycleLookupResponse]);

  const populatedCyclesById = useMemo(() => {
    const populatedCycles = new Map<string, PopulatedCheckinCycle>();
    const rows = cycleLookupResponse ? cycleLookupResponse.data || [] : [];

    rows.forEach((row) => {
      const submission = row as SubmissionRecord;
      const cycleId = getCheckinCycleId(submission);
      const populatedCycle = getPopulatedCheckinCycle(submission);
      if (cycleId && populatedCycle) {
        populatedCycles.set(cycleId, populatedCycle);
      }
    });

    return populatedCycles;
  }, [cycleLookupResponse]);

  // Build cycle options from user-scoped submissions instead of the admin-only
  // cycle-management endpoint.
  const checkinCycles = useMemo<ICheckinCycle[]>(() => {
    const cycles = new Map<string, ICheckinCycle>();

    allSubmissions.forEach((submission) => {
      const cycleId = getCheckinCycleId(submission);

      if (!cycleId || cycles.has(cycleId)) return;

      const populatedCycle = populatedCyclesById.get(cycleId);

      cycles.set(cycleId, {
        ...populatedCycle,
        id: cycleId,
        cycleName: resolveCheckinCycleName(submission, cycleNamesById),
        status:
          populatedCycle?.status ||
          (submission.isActive ? 'active' : 'ended'),
        createdBy: populatedCycle?.createdBy || '',
        createdAt: populatedCycle?.createdAt || submission.createdAt,
        updatedAt:
          populatedCycle?.updatedAt ||
          submission.submittedAt ||
          submission.createdAt,
      });
    });

    return Array.from(cycles.values());
  }, [allSubmissions, cycleNamesById, populatedCyclesById]);

  // Create cycles map for quick lookup
  const cyclesMap = useMemo(() => {
    const map = new Map<string, ICheckinCycle>();
    checkinCycles.forEach((cycle) => {
      map.set(cycle.id, cycle);
    });
    return map;
  }, [checkinCycles]);

  // Enrich checkins with cycle information
  const enrichedSubmissions = useMemo(() => {
    return allSubmissions.map((submission) => {
      // Handle both string cycle ID and object cycle
      const cycleId = getCheckinCycleId(submission);

      const cycle = cycleId ? cyclesMap.get(cycleId) : null;

      return {
        ...submission,
        cycleName:
          cycle?.cycleName ||
          resolveCheckinCycleName(submission, cycleNamesById),
        cycle: cycle
          ? {
              ...cycle,
            }
          : submission.cycle,
      };
    });
  }, [allSubmissions, cycleNamesById, cyclesMap]);

  return {
    // Enriched data
    enrichedSubmissions,
    checkinCycles,
    cyclesMap,

    // Loading states
    isLoading: checkinsLoading || cycleLookupLoading,
    cyclesLoading: cycleLookupLoading,
    checkinsLoading,

    // Refetch functions
    checkinsResponseRefetch,

    // Controller for additional operations
    checkinsController,

    // Raw data (in case needed)
    allSubmissions,
    checkinsResponse,
  };
};
